import { Fragment, MouseEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// material-ui
import { alpha, useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

// third-party
import {
  ColumnDef,
  HeaderGroup,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  useReactTable,
  SortingState,
  FilterFn,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";

// project-import
import ScrollX from "components/ScrollX";
import MainCard from "components/MainCard";
import IconButton from "components/@extended/IconButton";
import {
  HeaderSort,
  RowSelection,
  TablePagination,
} from "components/@extended/react-table";

import Loader from "components/Loader";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import { openSnackbar } from "api/snackbar";
import { SnackbarProps } from "types/snackbar";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import AlertCourseDelete from "./AlertCourseDelete";

export const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta(itemRank);
  return itemRank.passed;
};

function ReactTable({
  countPage,
  setSortBy,
  setPageSize,
  handleApplyFilters,
  pageSize,
  currentPage,
  handlePageChange,
  data,
  columns,
  searchText,
  setCurrenttPage,
  modalToggler,
  handleSearchChange,
  searchedtext,
  setsearchedtext,
  loadingSearsh,
}: any) {
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("sm"));

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "id",
      desc: false,
    },
  ]);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      rowSelection,
      globalFilter,
    },
    enableRowSelection: true,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getRowCanExpand: () => true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    globalFilterFn: fuzzyFilter,
    debugTable: true,
  });

  const backColor = alpha(theme.palette.primary.light, 0.1);
  const intl = useIntl();

  const handleAddCourse = () => {
    window.location.href = `/training-courses/add-course`;
  };

  return (
    <MainCard content={false}>
      <Grid container direction={"column"}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            padding: 2,
            ...(matchDownSM && {
              "& .MuiOutlinedInput-root, & .MuiFormControl-root": {
                width: "100%",
              },
            }),
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              variant="outlined"
              value={searchedtext}
              onChange={(e) => setsearchedtext(e.target.value)}
              placeholder={intl.formatMessage({
                id: "Search records..",
                defaultMessage: "Search records..",
              })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" onClick={handleSearchChange}>
              {loadingSearsh === false ? (
                <FormattedMessage id="Search" />
              ) : (
                <CircularProgress size="1.5rem" style={{ color: "white" }} />
              )}
            </Button>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                startIcon={<PlusOutlined />}
                onClick={handleAddCourse}
              >
                <FormattedMessage id="Add New" />
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Grid>

      <ScrollX>
        <Stack>
          <RowSelection selected={Object.keys(rowSelection).length} />
          <TableContainer>
            <Table>
              <TableHead>
                {table
                  .getHeaderGroups()
                  .map((headerGroup: HeaderGroup<any>) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableCell
                          key={header.id}
                          {...header.column.columnDef.meta}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.isPlaceholder ? null : (
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Box>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </Box>
                              {header.column.getCanSort() && (
                                <HeaderSort column={header.column} />
                              )}
                            </Stack>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <TableRow>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          {...cell.column.columnDef.meta}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <>
            <Divider />
            <Box sx={{ p: 2 }}>
              <TablePagination
                {...{
                  setPageSize: setPageSize,
                  setPageIndex: setCurrenttPage,
                  getState: table.getState,
                  getPageCount: () => countPage.last_page,
                  currentPage: currentPage,
                  handlePageChange: handlePageChange,
                  initialPageSize: pageSize,
                }}
              />
            </Box>
          </>
        </Stack>
      </ScrollX>
    </MainCard>
  );
}

const TrainingCourses = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any[]>([]);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("");
  const [searchedtext, setsearchedtext] = useState("");
  const [loadingSearsh, setloadingSearsh] = useState(false);
  const [courseDeleteId, setCourseDeleteId] = useState<any>("");
  const [open, setOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleClose = () => {
    setOpen(!open);
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">{row?.original.id}</Typography>
            </Stack>
          </Stack>
        ),
      },
      //   {
      //     header: () => <FormattedMessage id="user_name" />,
      //     accessorKey: "user_name",
      //     cell: ({ row, getValue }) => {
      //       return (
      //         <Stack direction="row" spacing={1.5} alignItems="center">
      //           <Stack spacing={0}>{row.original.user_name}</Stack>
      //         </Stack>
      //       );
      //     },
      //   },
      {
        header: () => <FormattedMessage id="Title" />,
        accessorKey: "title",
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>{row.original.title}</Stack>
          </Stack>
        ),
      },

      {
        header: () => <FormattedMessage id="course_period" />,
        accessorKey: "course_period",
        cell: ({ row, getValue }) => {
          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack spacing={0}>{row.original.course_period}</Stack>
            </Stack>
          );
        },
      },
      {
        header: () => <FormattedMessage id="course_type" />,
        accessorKey: "course_type",
        cell: ({ row, getValue }) => {
          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack spacing={0}>{row.original.course_type}</Stack>
            </Stack>
          );
        },
      },
      {
        header: () => <FormattedMessage id="start_date" />,
        accessorKey: "start_date",
        cell: ({ row, getValue }) => {
          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack spacing={0}>{row.original.start_date}</Stack>
            </Stack>
          );
        },
      },
      {
        header: () => <FormattedMessage id="end_date" />,
        accessorKey: "end_date",
        cell: ({ row, getValue }) => {
          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack spacing={0}>{row.original.end_date}</Stack>
            </Stack>
          );
        },
      },
      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "status",
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              color={row.original.status ? "success" : "error"}
              label={
                row.original.status ? (
                  <FormattedMessage id="Published" />
                ) : (
                  <FormattedMessage id="UnPublished" />
                )
              }
              size="small"
              variant="filled"
            />
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        disableSortBy: true,
        cell: ({ row }) => {
          const { id } = row.original;
          const handleEditCourse = () => {
            window.location.href = `/training-courses/edit-course/${id}`;
          };
          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title={<FormattedMessage id="edit" />}>
                <IconButton
                  color="primary"
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleEditCourse();
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setCourseDeleteId(row.original.id);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    []
  );

  const fetchData = () => {
    setLoading(true);

    const urlParams = new URLSearchParams(window.location.search);
    const companyId = urlParams.get("company_id");

    USERAXIOS.get(
      `/courses?page=${currentPage}${
        searchQuery ? `&title=${searchQuery}` : ""
      }${companyId ? `&company_id=${companyId}` : ""}`
    )
      .then((response) => {
        const { data, meta } = response.data;
        const fetchedCourses = data;
        const rowsWithKeys = fetchedCourses.map((course) => ({
          ...course,
          key: course.id,
        }));
        setResult(rowsWithKeys);
        setLoading(false);
        setCountPage(meta);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching training courses:", error);
        openSnackbar({
          open: true,
          message: "Error fetching training courses",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "error",
          },
        } as SnackbarProps);
      });
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchQuery]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page);
  };

  const handleSearch = () => {
    setloadingSearsh(true);
    setCurrenttPage(1);
    setSearchQuery(searchedtext);
    setloadingSearsh(false);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <ReactTable
          setSortBy={setSortBy}
          sortBy={sortBy}
          pageSize={pageSize}
          setPageSize={setPageSize}
          data={result}
          columns={columns}
          countPage={countPage}
          currentPage={currentPage}
          setCurrenttPage={setCurrenttPage}
          handlePageChange={handlePageChange}
          searchText={searchedtext}
          handleSearchChange={handleSearch}
          searchedtext={searchedtext}
          setsearchedtext={setsearchedtext}
          loadingSearsh={loadingSearsh}
        />
      )}
      <AlertCourseDelete
        setOpen={setOpen}
        setResult={setResult}
        fetchData={fetchData}
        id={courseDeleteId}
        title={courseDeleteId}
        open={open}
        handleClose={handleClose}
      />
    </>
  );
};

export default TrainingCourses;
